import Card from '@/components/Card';
import {
  CuCalendarIcon,
  CuCheckboxIcon,
  CuDropdownIcon,
  CuNumberIcon,
  CuTextareaIcon,
  CuTextIcon,
} from '@/components/Icons/CustomReactIcons';
import PERMISSIONS from '@/constants/permissions';
import {ExtraFieldTypeModel} from '@/typings/models/ExtraFields';
import {CheckAccess} from '@/utils/access';
import {useHasAccess} from '@/utils/hooks';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import {EditOutlined, HolderOutlined, LockOutlined, PlusOutlined} from '@ant-design/icons';
import {Badge, Col, Row, Space, Tag, Tooltip} from 'antd';
import {reorderExtraFieldTypeRequest} from '@/services/api/extraFieldType';
import React, {useEffect, useState} from 'react';
import {useDispatch, useIntl} from 'umi';
import styles from './index.less';
import {EXTRA_FIELD_VALUE_TYPES} from './types';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

interface Props {
  extraFields: ExtraFieldTypeModel[];
  onEdit: (field: ExtraFieldTypeModel) => void;
  onCreate: () => void;
  sortable?: boolean;
  relatedTo?: string;
}

type NewFieldCard = {
  uuid: 'NEW';
};

export const ICON_MAP = {
  [EXTRA_FIELD_VALUE_TYPES.TEXT]: CuTextIcon,
  [EXTRA_FIELD_VALUE_TYPES.NUMBER]: CuNumberIcon,
  [EXTRA_FIELD_VALUE_TYPES.NUMBER_RANGE]: CuNumberIcon,
  [EXTRA_FIELD_VALUE_TYPES.BOOL]: CuCheckboxIcon,
  [EXTRA_FIELD_VALUE_TYPES.MULTI_SELECT]: CuDropdownIcon,
  [EXTRA_FIELD_VALUE_TYPES.SELECT]: CuDropdownIcon,
  [EXTRA_FIELD_VALUE_TYPES.DATE]: CuCalendarIcon,
  [EXTRA_FIELD_VALUE_TYPES.TEXTAREA]: CuTextareaIcon,
  [EXTRA_FIELD_VALUE_TYPES.JSON]: CuTextareaIcon,
};

export const TRANSLATION_MAP = (intl: any) => ({
  [EXTRA_FIELD_VALUE_TYPES.TEXT]: intl.formatMessage({
    id: 'pages.extraFieldType.text',
    defaultMessage: 'Text',
  }),
  [EXTRA_FIELD_VALUE_TYPES.NUMBER]: intl.formatMessage({
    id: 'pages.extraFieldType.number',
    defaultMessage: 'Number',
  }),
  [EXTRA_FIELD_VALUE_TYPES.NUMBER_RANGE]: intl.formatMessage({
    id: 'pages.extraFieldType.numberRange',
    defaultMessage: 'Range',
  }),
  [EXTRA_FIELD_VALUE_TYPES.BOOL]: intl.formatMessage({
    id: 'pages.extraFieldType.bool',
    defaultMessage: 'Boolean',
  }),
  [EXTRA_FIELD_VALUE_TYPES.MULTI_SELECT]: intl.formatMessage({
    id: 'pages.extraFieldType.multi_select',
    defaultMessage: 'Multi Select',
  }),
  [EXTRA_FIELD_VALUE_TYPES.SELECT]: intl.formatMessage({
    id: 'pages.extraFieldType.select',
    defaultMessage: 'Select',
  }),
  [EXTRA_FIELD_VALUE_TYPES.DATE]: intl.formatMessage({
    id: 'pages.extraFieldType.date',
    defaultMessage: 'Date',
  }),
  [EXTRA_FIELD_VALUE_TYPES.TEXTAREA]: intl.formatMessage({
    id: 'pages.extraFieldType.textarea',
    defaultMessage: 'Textarea',
  }),
  [EXTRA_FIELD_VALUE_TYPES.JSON]: intl.formatMessage({
    id: 'pages.extraFieldType.json',
    defaultMessage: 'JSON',
  }),
});

const FieldIcon = ({ fieldType }: { fieldType: string }) => {
  const intl = useIntl();
  const IconComponent = ICON_MAP[fieldType] || CuTextIcon;

  return (
    <div className={styles.iconContainer}>
      <IconComponent />
      <span>{TRANSLATION_MAP(intl)[fieldType] || fieldType}</span>
    </div>
  );
};

const RELATED_TO_LABELS = {
  result: (
    <F id="pages.extraFieldType.relatedToResult" defaultMessage="Result" />
  ),
  customer: (
    <F
      id="pages.extraFieldType.relatedToCustomer"
      defaultMessage="Customer"
    />
  ),
  serviceRequest: (
    <F id="entity.appointment.singular" defaultMessage="Appointment" />
  ),
  branch: <F id="entity.branch.singular" defaultMessage="Branch" />,
  branchService: <F id="entity.service.singular" defaultMessage="Service" />,
  serviceType: (
    <F id="entity.serviceType.singular" defaultMessage="Service Type" />
  ),
};

const ExtraFieldCard = ({
  field,
  onEdit,
  sortable,
  dragHandleProps,
  style,
  setNodeRef,
}: {
  field: ExtraFieldTypeModel;
  onEdit: (field: ExtraFieldTypeModel) => void;
  sortable?: boolean;
  dragHandleProps?: any;
  style?: React.CSSProperties;
  setNodeRef?: (node: HTMLElement | null) => void;
}) => {
  return (
    <Col
      ref={setNodeRef}
      style={style}
      xs={24}
      sm={12}
      md={12}
      lg={8}
      xl={8}
      xxl={6}
    >
      <Tooltip
        title={
          <>
            {field.label}
            {field.description ? (
              <div className={styles.fieldTypeTooltipDesc}>{field.description}</div>
            ) : null}
          </>
        }
      >
        <Card mini className={styles.fieldTypeCard}>
          <div className={styles.cardHeader}>
            <div className={styles.cardHeaderMain}>
              <div className={styles.title}>
                {sortable && (
                  <div {...dragHandleProps} className={styles.dragHandle}>
                    <HolderOutlined />
                  </div>
                )}
                <div className={styles.titleText}>{field.label}</div>
                {field.is_confidential && (
                  <Tooltip title={<F id="pages.extraFieldType.isConfidential" defaultMessage="Confidential" />}>
                    <LockOutlined className={styles.confidentialIcon} />
                  </Tooltip>
                )}
              </div>
              <Tag
                color="purple"
                style={{
                  fontSize: '10px',
                  lineHeight: '12px',
                  padding: '0 8px',
                  borderRadius: '10px',
                }}
                bordered={false}
              >
                {RELATED_TO_LABELS[field.related_to] || field.related_to}
              </Tag>
            </div>
            <Badge color="blue" count={field.options?.length} size="small">
              <FieldIcon fieldType={field.field_type} />
            </Badge>
          </div>

          <CheckAccess
            access={[PERMISSIONS.ExtraFieldType.EDIT, PERMISSIONS.BUSINESS_PARTNER]}
          >
            <div onClick={() => onEdit(field)} className={styles.editOverlay}>
              <EditOutlined className={styles.editIcon} />
            </div>
          </CheckAccess>
        </Card>
      </Tooltip>
    </Col>
  );
};

const SortableExtraFieldCard = ({
  field,
  onEdit,
  sortable,
}: {
  field: ExtraFieldTypeModel;
  onEdit: (field: ExtraFieldTypeModel) => void;
  sortable?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.uuid, disabled: !sortable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ExtraFieldCard
      field={field}
      onEdit={onEdit}
      sortable={sortable}
      dragHandleProps={{ ...attributes, ...listeners }}
      style={style}
      setNodeRef={setNodeRef}
    />
  );
};

const ExtraFieldsList: React.FC<Props> = ({
  extraFields,
  onEdit,
  onCreate,
  sortable = true,
  relatedTo,
}) => {
  const dispatch = useDispatch();
  const [items, setItems] = useState<ExtraFieldTypeModel[]>([]);

  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    setItems(extraFields);
  }, [extraFields]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setActiveId(null);

    if (active && over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.uuid === active.id);
      const newIndex = items.findIndex((item) => item.uuid === over.id);

      const newOrderedItems = arrayMove(items, oldIndex, newIndex);
      const order = newOrderedItems.map((item) => item.uuid);

      setItems(newOrderedItems);
      dispatch({ type: 'extraFields/reorderTypes', payload: { relatedTo, order } });

      reorderExtraFieldTypeRequest({
        params: { relatedTo: relatedTo! },
        data: { extra_field_types: order },
      });
    }
  };

  const hasCreateExtraFieldTypeAccess = useHasAccess([
    PERMISSIONS.ExtraFieldType.CREATE,
    PERMISSIONS.BUSINESS_PARTNER,
  ]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {hasCreateExtraFieldTypeAccess && (
          <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={6}>
            <Card
              mini
              className={styles.newEditableFieldType}
              onClick={onCreate}
            >
              <Space>
                <PlusOutlined />
                <F
                  entity={INTL_ENTITY.EXTRA_FIELD}
                  action={INTL_ACTION.CREATE}
                />
              </Space>
            </Card>
          </Col>
        )}
        <SortableContext
          items={items.map((i) => i.uuid)}
          strategy={rectSortingStrategy}
        >
          {items.map((field) => (
            <SortableExtraFieldCard
              key={field.uuid}
              field={field}
              onEdit={onEdit}
              sortable={sortable}
            />
          ))}
        </SortableContext>
      </Row>
      <DragOverlay>
        {activeId ? (
          <ExtraFieldCard
            field={items.find((i) => i.uuid === activeId)!}
            onEdit={onEdit}
            sortable={sortable}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ExtraFieldsList;
